import { useState } from 'react';
import type { CostCode } from '../lib/costcodes';
import type { SummaryCostCode } from '../lib/reports';
import { formatRs } from '../lib/money';

/**
 * Cost by cost code — the report's pie ("I need a pie chart in reports that
 * show cost for each cost code", 11 October 2026). A ring rather than a full
 * disc, so the total sits in the middle and a hovered slice can say its own.
 *
 * Hand-rolled SVG like CostChart, for the same reason (rule 14: nothing from a
 * CDN, and a library buys nothing for one ring). Built to the same rules:
 *   * colour follows the CODE, not its size: a code takes the slot of its
 *     place in the owner's list, so it is the same colour in every period;
 *   * six categorical slots (`--cc-1` … `--cc-6`, validated in both modes and
 *     round the ring's wrap). Codes after the sixth fold into "Other codes";
 *     cost with no code is "No cost code". Both are neutral greys;
 *   * 2px surface gap between slices; text wears text tokens;
 *   * a legend always, with each code's amount and share, and the table under
 *     it lists every code on its own — identity is never colour alone.
 */

interface Slice {
  key: string;
  label: string;
  name: string;
  totalRs: number;
  colour: string;
}

const SLOTS = 6;
const SIZE = 240;
const OUTER = 112;
const INNER = 68;

function arc(start: number, end: number): string {
  const c = SIZE / 2;
  const at = (radius: number, angle: number) =>
    `${(c + radius * Math.cos(angle)).toFixed(2)} ${(c + radius * Math.sin(angle)).toFixed(2)}`;
  const large = end - start > Math.PI ? 1 : 0;
  return [
    `M${at(OUTER, start)}`,
    `A${OUTER} ${OUTER} 0 ${large} 1 ${at(OUTER, end)}`,
    `L${at(INNER, end)}`,
    `A${INNER} ${INNER} 0 ${large} 0 ${at(INNER, start)}`,
    'Z',
  ].join('');
}

/** One slice per coded row in the first six, then Other codes, then No cost code. */
export function pieSlices(rows: SummaryCostCode[], codes: CostCode[]): Slice[] {
  const slotOf = (id: string) => codes.findIndex((code) => code.id === id);
  const coded: (Slice & { slot: number })[] = [];
  let otherRs = 0;
  let noneRs = 0;
  for (const row of rows) {
    if (!row.cost_code_id) {
      noneRs += row.total_rs;
      continue;
    }
    const slot = slotOf(row.cost_code_id);
    if (slot < 0 || slot >= SLOTS) {
      otherRs += row.total_rs;
      continue;
    }
    coded.push({
      key: row.cost_code_id,
      label: row.code ?? '—',
      name: row.name ?? '',
      totalRs: row.total_rs,
      colour: `var(--cc-${slot + 1})`,
      slot,
    });
  }
  coded.sort((a, b) => a.slot - b.slot);
  const slices: Slice[] = coded.map(({ slot: _slot, ...slice }) => slice);
  if (otherRs > 0) {
    slices.push({ key: 'other', label: 'Other codes', name: '', totalRs: otherRs, colour: 'var(--cc-other)' });
  }
  if (noneRs > 0) {
    slices.push({ key: 'none', label: 'No cost code', name: '', totalRs: noneRs, colour: 'var(--cc-none)' });
  }
  return slices;
}

export function share(part: number, whole: number): string {
  return whole ? `${((part / whole) * 100).toFixed(1)}%` : '0%';
}

export default function CostCodePie({ rows, codes }: { rows: SummaryCostCode[]; codes: CostCode[] }) {
  const [hover, setHover] = useState<string | null>(null);
  const slices = pieSlices(rows, codes);
  const total = slices.reduce((sum, slice) => sum + slice.totalRs, 0);
  const current = slices.find((slice) => slice.key === hover) ?? null;

  let angle = -Math.PI / 2;
  const drawn = slices.map((slice) => {
    const sweep = total ? (slice.totalRs / total) * Math.PI * 2 : 0;
    const start = angle;
    angle += sweep;
    // A single slice is the whole ring: two halves, since one arc cannot
    // start and end on the same point.
    const d = sweep >= Math.PI * 2 - 1e-6
      ? `${arc(start, start + Math.PI)}${arc(start + Math.PI, start + Math.PI * 2)}`
      : arc(start, angle);
    return { ...slice, d };
  });

  const description = slices
    .map((slice) => `${slice.label} ${share(slice.totalRs, total)}`)
    .join(', ');

  return (
    <div className="ccpie">
      <div className="ccpie-ring">
        <svg
          width={SIZE}
          height={SIZE}
          viewBox={`0 0 ${SIZE} ${SIZE}`}
          role="img"
          aria-label={`Cost by cost code: ${description}`}
        >
          {drawn.map((slice) => (
            <path
              key={slice.key}
              d={slice.d}
              fill={slice.colour}
              stroke="var(--surface)"
              strokeWidth={2}
              strokeLinejoin="round"
              opacity={hover === null || hover === slice.key ? 1 : 0.35}
              onMouseEnter={() => setHover(slice.key)}
              onMouseLeave={() => setHover(null)}
            />
          ))}
        </svg>
        <div className="ccpie-centre" aria-hidden="true">
          <div className="k">{current ? current.label : 'Total cost'}</div>
          <div className="v">{formatRs(current ? current.totalRs : total)}</div>
          <div className="k">
            {current ? `${share(current.totalRs, total)} of cost` : 'all costs'}
          </div>
        </div>
      </div>

      <div className="ccpie-legend">
        {slices.map((slice) => (
          <button
            type="button"
            key={slice.key}
            className="ccpie-item"
            style={{ opacity: hover === null || hover === slice.key ? 1 : 0.5 }}
            onMouseEnter={() => setHover(slice.key)}
            onMouseLeave={() => setHover(null)}
            onFocus={() => setHover(slice.key)}
            onBlur={() => setHover(null)}
          >
            <span className="swatch" style={{ background: slice.colour }} />
            <span className="ccpie-code">{slice.label}</span>
            <span className="ccpie-name">{slice.name}</span>
            <span className="ccpie-rs">{formatRs(slice.totalRs)}</span>
            <span className="ccpie-pct">{share(slice.totalRs, total)}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
